import React, { useEffect } from 'react'
import {
  Stack,
  HStack,
  VStack,
  Image,
  Link,
  Button,
  Text,
  Heading,
  Spacer,
} from '@babylon/web-platform-ui'
import {
  Text as MedkitText,
  LinkAnchor,
  EditorialLayout,
  ButtonLink,
  AppDownloadButton,
} from '@babylon/medkit'
import { useFormatMessage } from '@babylon/intl'
import AppsInfo from '@/components/AppsInfo'
import {
  appStores,
  AppStoreLinks,
  appStoreTrackingNames,
} from '@/config/constants'
import { useCurrentPageContent } from '@/redux/signup/selectors'
import { useRegionInfo } from '@/redux/regionInfo/selectors'
import { trackTealiumEvent } from '@/tracking'
import { useTrackingScreenTitle } from '@/hooks'
import { useUserId } from '@/redux/userProfile/selectors'
import { Summary } from '@/types/partnerConfig'
import {
  useBookUrl,
  useCTAs,
  useWebAppUrl,
} from '@/redux/productConfig/selectors'
import { applyStringReplacers } from '@/utils'
import { useConfig } from '@/redux/partnerConfig/selectors'
import appsInfoMessages from '../../components/AppsInfo/AppsInfo.messages'
import messages from './SummaryPage.messages'

const CTATypes = {
  // The Link component renders a LinkButton if an onClick prop is passed,
  // and the href doesn't get opened, so we have to use LinkAnchor instead.
  Link: LinkAnchor,
  ButtonLink,
}

const getStoreUrl = (device: keyof AppStoreLinks, region: string) =>
  appStores[region][device]

const SummaryPage = () => {
  const content = useCurrentPageContent()
  const { useDnaV2Design } = useConfig()
  const region = useRegionInfo()
  // eslint-disable-next-line camelcase
  const regionIsoCode = region?.iso_code
  const webAppUrl = useWebAppUrl()
  const stepTrackingName = useTrackingScreenTitle()
  const userId = useUserId()
  const translate = useFormatMessage()

  const {
    cta = {},
    imgUrl,
    dnaV2DesignImgUrl,
    contactDetails,
  } = content as Summary
  const {
    bookCtaBody,
    bookCtaBtn,
    bookCtaTitle,
    downloadAppTitle,
    downloadAppBody,
  } = messages
  const { label, link, webAppPartialLink, type } = cta

  // We often want to redirect the user to the WebApp.
  // This is a way to provide a partial path to a specific WebApp page
  const webAppHref = `${webAppUrl}${webAppPartialLink}`
  const ctaHref = link || webAppHref
  const hasCta = label && ctaHref
  const ctaType = type || 'Link'
  const CtaComponent = CTATypes[ctaType]

  const onAppStoreButtonClick = (device: keyof AppStoreLinks) => {
    const trackingName = appStoreTrackingNames[device]

    if (trackingName) {
      trackTealiumEvent({
        eventAction: 'clicked download app',
        eventCategory: 'signup',
        eventLabel: trackingName,
        screen_title: 'signup/thank-you',
        tealium_event: 'download app',
      })
    }
  }

  const trackCtaClick = () => {
    trackTealiumEvent({
      screen_title: stepTrackingName,
      eventAction: 'clicked partner cta',
      eventCategory: 'signup',
      eventLabel: label?.toLowerCase(),
      destination: ctaHref,
      tealium_event: `clicked ${label?.toLowerCase()} cta`,
      recipientId: userId,
    })
  }

  const downloadButtons = cta?.showDownloadButtons
  // #region book appointment CTA
  const showBookCTA = useCTAs()?.includes('book_appointment')

  const Card = React.lazy(
    () => import(/* webpackChunkName: "BookCTA" */ '../../components/Card/Card')
  )
  const CardGroup = React.lazy(
    () =>
      import(
        /* webpackChunkName: "BookCTA" */ '../../components/Card/CardGroup'
      )
  )
  const bookUrl = useBookUrl()
  const onClickBookCta = () => {
    trackTealiumEvent({
      tealium_event: 'clicked book appointment',
      screen_title: stepTrackingName,
      eventCategory: 'signup',
      eventAction: 'clicked book appointment',
    })
    window.location.href = bookUrl
  }

  useEffect(() => {
    if (showBookCTA) {
      trackTealiumEvent({
        tealium_event: 'saw book appointment',
        screen_title: stepTrackingName,
        eventCategory: 'signup',
        eventAction: 'saw book appointment',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // #endregion

  if (useDnaV2Design) {
    return (
      <>
        <Stack direction={{ base: 'column', md: 'row' }}>
          <VStack spacing={6} align="start">
            <Stack>
              {showBookCTA && (
                <Stack>
                  <React.Suspense fallback="<div>Loading</div>">
                    <CardGroup variant="group">
                      <Card
                        heading={translate(bookCtaTitle)}
                        body={translate(bookCtaBody)}
                        actions={{
                          button1: {
                            label: translate(bookCtaBtn),
                            onPress: onClickBookCta,
                            accessibilityRole: 'link',
                          },
                        }}
                      />
                    </CardGroup>
                  </React.Suspense>
                  <Spacer />
                  <Spacer />
                  <Spacer />
                </Stack>
              )}

              {showBookCTA && (
                <VStack align="flex-start">
                  <Heading size="xs">{translate(downloadAppTitle)}</Heading>
                  <Text>{translate(downloadAppBody)}</Text>
                </VStack>
              )}

              {hasCta && ctaType === 'Link' && (
                <Link href={ctaHref}>{label}</Link>
              )}
              {hasCta && ctaType === 'ButtonLink' && (
                <Button onClick={() => {}}>{label}</Button>
              )}
              {contactDetails?.map((copy, index) => (
                <Text key={`copy-${index + 1}`}>
                  {applyStringReplacers(copy, Boolean(useDnaV2Design))}
                </Text>
              ))}

              {regionIsoCode && (
                <>
                  {downloadButtons && (
                    <>
                      <Spacer />
                      <HStack align="start" data-testid="appDownload-buttons">
                        <AppDownloadButton
                          href={getStoreUrl('apple', regionIsoCode!)}
                          title={translate(appsInfoMessages.iosDownloadAltText)}
                          onClick={() => onAppStoreButtonClick('apple')}
                          vendor="AppStore"
                        />
                        <AppDownloadButton
                          href={getStoreUrl('android', regionIsoCode!)}
                          title={translate(appsInfoMessages.iosDownloadAltText)}
                          onClick={() => onAppStoreButtonClick('android')}
                          vendor="PlayStore"
                        />
                      </HStack>
                    </>
                  )}
                </>
              )}
            </Stack>
          </VStack>
          {dnaV2DesignImgUrl && !showBookCTA && (
            <Image src={dnaV2DesignImgUrl} alt="" data-testid="appinfo-image" />
          )}
        </Stack>
      </>
    )
  }

  return (
    <>
      {showBookCTA && (
        <Stack marginTop={24}>
          <React.Suspense fallback="<div>Loading</div>">
            <CardGroup variant="group">
              <Card
                heading={translate(bookCtaTitle)}
                body={translate(bookCtaBody)}
                actions={{
                  button1: {
                    label: translate(bookCtaBtn),
                    onPress: onClickBookCta,
                    accessibilityRole: 'link',
                  },
                }}
              />
            </CardGroup>
          </React.Suspense>
        </Stack>
      )}

      <EditorialLayout>
        {downloadButtons && (
          <>
            <MedkitText variant="h5">{translate(downloadAppTitle)}</MedkitText>
            <MedkitText variant="body">{translate(downloadAppBody)}</MedkitText>
          </>
        )}

        {hasCta && !useDnaV2Design && (
          <MedkitText variant="body">
            <CtaComponent
              href={ctaHref}
              title={label}
              fullWidth
              onClick={() => trackCtaClick()}
            >
              {label}
            </CtaComponent>
          </MedkitText>
        )}
        {hasCta && useDnaV2Design && (
          <MedkitText variant="body">
            {ctaType === 'Link' && (
              <Link
                href={ctaHref}
                title={label}
                onClick={() => trackCtaClick()}
              >
                {label}
              </Link>
            )}
            {ctaType === 'ButtonLink' && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link as="button" type="button" onClick={() => trackCtaClick()}>
                {label}
              </Link>
            )}
          </MedkitText>
        )}
        {contactDetails?.map((copy, index) => (
          <MedkitText
            variant="body"
            key={`copy-${index + 1}`}
            data-testid="contactDetails"
          >
            {applyStringReplacers(copy, Boolean(useDnaV2Design))}
          </MedkitText>
        ))}
      </EditorialLayout>
      {regionIsoCode && (
        <AppsInfo
          downloadButtons={cta?.showDownloadButtons}
          image={showBookCTA ? undefined : imgUrl}
          region={regionIsoCode!}
          onAppStoreButtonClick={onAppStoreButtonClick}
        />
      )}
    </>
  )
}

export default SummaryPage
